import React from "react";
import { BadgeCheck, CircleCheck, Crown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type TierType = "free" | "verified" | "premium";

export function TierBadge({
  tier,
  verificationStatus,
  showTooltip = true,
  size = "small",
  minimal = false,
}: {
  tier: TierType;
  verificationStatus?: string;
  showTooltip?: boolean;
  size?: "small" | "medium";
  minimal?: boolean;
}) {
  if (minimal && tier === "free" && verificationStatus !== "verified") return null;

  const config = {
    verified: {
      label: "Verified Student",
      variant: "success" as const,
      Icon: BadgeCheck,
      tooltip: "Verified Student — full institutional access",
    },
    premium: {
      label: "Premium Career",
      variant: "warning" as const,
      Icon: Crown,
      tooltip: "Premium Member — career acceleration access",
    },
    free: {
      label: verificationStatus === "verified" ? "Free (Verified)" : "Free Tier",
      variant: "outline" as const,
      Icon: CircleCheck,
      tooltip: verificationStatus === "verified" ? "Verified status (Free tier)" : "Free tier",
    },
  };

  const { label, variant, Icon, tooltip } = config[tier] ?? config.free;

  const content = minimal ? (
    <span
      className={cn(
        "inline-flex items-center",
        variant === "success" && "text-success",
        variant === "warning" && "text-warning",
        variant === "outline" && "text-body",
      )}
      aria-label={label}
    >
      <Icon className={size === "small" ? "size-3.5" : "size-4"} />
    </span>
  ) : (
    <Badge variant={variant} className={size === "medium" ? "px-2 py-0.5 text-[13px]" : undefined}>
      <Icon />
      {label}
    </Badge>
  );

  if (!showTooltip) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
}
